import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getProviderEarnings } from '../../utils/stripe';

const EarningsDashboard = () => {
  const { currentUser } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [userProfile, setUserProfile] = useState(null);


  useEffect(() => {
    if (currentUser) {
      fetchUserProfile();
      fetchPayouts();
    }
  }, [currentUser]);

  const fetchUserProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setUserProfile(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      
      const payoutsQuery = query(
        collection(db, 'payouts'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const payoutsSnapshot = await getDocs(payoutsQuery);
      let payoutsData = payoutsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Update payout statuses based on business logic
      payoutsData = await updatePayoutStatuses(payoutsData);
      
      setPayouts(payoutsData);
    } catch (error) {
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePayoutStatuses = async (payoutsData) => {
    const now = new Date();
    const batch = writeBatch(db);
    const updates = [];

    for (const payout of payoutsData) {
      let newStatus = payout.status;
      
      // Skip payouts that are already cancelled or paid
      if (['Cancelled', 'Paid'].includes(payout.status)) {
        continue;
      }

      // Get the settlement date
      const settledDate = payout.settledDate?.seconds 
        ? new Date(payout.settledDate.seconds * 1000) 
        : new Date(payout.settledDate);

      // Check if the session still exists and its status
      if (payout.sessionId) {
        try {
          const sessionDoc = await getDoc(doc(db, 'sessions', payout.sessionId));
          if (sessionDoc.exists()) {
            const sessionData = sessionDoc.data();
            
            // If session is cancelled, update payout to Cancelled
            if (sessionData.status === 'cancelled') {
              newStatus = 'Cancelled';
            } else {
              // Check if 4 hours have passed since the session started (past cancellation period)
              const sessionDateTime = new Date(`${sessionData.scheduledDate}T${sessionData.scheduledTime}`);
              const fourHoursAfterSession = new Date(sessionDateTime.getTime() + (4 * 60 * 60 * 1000));
              
              if (now >= fourHoursAfterSession && payout.status === 'Not Visible') {
                newStatus = 'Not Settled';
              }
            }
          }
        } catch (error) {
          console.error('Error checking session for payout:', payout.id, error);
        }
      }

      // If settlement date has passed, update to Settled
      if (now >= settledDate && ['Not Visible', 'Not Settled'].includes(payout.status)) {
        newStatus = 'Settled';
      }

      // Update if status changed
      if (newStatus !== payout.status) {
        const payoutRef = doc(db, 'payouts', payout.id);
        batch.update(payoutRef, {
          status: newStatus,
          updatedAt: new Date()
        });
        
        // Update local data
        payout.status = newStatus;
        updates.push(payout.id);
      }
    }

    // Commit batch updates if any
    if (updates.length > 0) {
      try {
        await batch.commit();
        console.log(`Updated ${updates.length} payout statuses`);
      } catch (error) {
        console.error('Error updating payout statuses:', error);
      }
    }

    return payoutsData;
  };

  const getPayoutStats = () => {
    const settledPayouts = payouts.filter(p => p.status === 'Settled');
    const notSettledPayouts = payouts.filter(p => p.status === 'Not Settled');
    const paidPayouts = payouts.filter(p => p.status === 'Paid');
    const cancelledPayouts = payouts.filter(p => p.status === 'Cancelled');

    const settledAmount = settledPayouts.reduce((sum, p) => sum + p.amount, 0);
    const notSettledAmount = notSettledPayouts.reduce((sum, p) => sum + p.amount, 0);
    const paidAmount = paidPayouts.reduce((sum, p) => sum + (p.paidAmount || p.amount), 0);
    const totalEarned = [...settledPayouts, ...notSettledPayouts, ...paidPayouts].reduce((sum, p) => sum + p.amount, 0);

    return {
      totalPayouts: payouts.length,
      totalEarned,
      settledAmount,
      notSettledAmount,
      paidAmount,
      settledCount: settledPayouts.length,
      notSettledCount: notSettledPayouts.length,
      paidCount: paidPayouts.length,
      cancelledCount: cancelledPayouts.length,
      availableForPayout: settledAmount + notSettledAmount
    };
  };

  const handleProcessPayout = async (payoutType) => {
    if (!userProfile?.stripeConnectAccountId) {
      alert('Please set up your Stripe Connect account first in your provider profile.');
      return;
    }

    const stats = getPayoutStats();
    const amount = payoutType === 'settled' ? stats.settledAmount : (stats.settledAmount + stats.notSettledAmount);
    
    if (amount <= 0) {
      alert(`No ${payoutType === 'settled' ? 'settled' : 'available'} funds to transfer.`);
      return;
    }

    const feeInfo = payoutType === 'not_settled' ? ` (2% fee will apply: ${formatCurrency(amount * 0.02)})` : '';
    const transferAmount = payoutType === 'not_settled' ? amount * 0.98 : amount;
    
    const confirmed = window.confirm(
      `Process ${payoutType === 'settled' ? 'settled' : 'all available'} payout of ${formatCurrency(amount)}?\n\n` +
      `You will receive: ${formatCurrency(transferAmount)}${feeInfo}\n\n` +
      `This will transfer funds to your connected bank account.`
    );

    if (!confirmed) return;

    setProcessingPayout(true);
    try {
      const { processProviderPayout } = await import('../../utils/stripe');
      
      const result = await processProviderPayout(payoutType);
      
      // Refresh payouts to show updated status
      await fetchPayouts();
      
      alert(
        `Payout processed successfully!\n\n` +
        `Amount: ${formatCurrency(result.transferAmount)}\n` +
        `Fee: ${formatCurrency(result.feeAmount)}\n` +
        `Payouts processed: ${result.payoutsProcessed}\n\n` +
        `Funds will arrive in your bank account within 1-2 business days.`
      );
    } catch (error) {
      console.error('Error processing payout:', error);
      let errorMessage = 'Failed to process payout. ';
      
      if (error.code === 'failed-precondition') {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again or contact support.';
      }
      
      alert(errorMessage);
    } finally {
      setProcessingPayout(false);
    }
  };

  const getVisiblePayouts = () => {
    return payouts.filter(p => ['Settled', 'Not Settled', 'Paid'].includes(p.status));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long'
    }).format(new Date(year, month - 1));
  };

  const stats = getPayoutStats();

  if (loading) {
    return <div className="loading">Loading earnings data...</div>;
  }

  return (
    <div className="earnings-dashboard">
      <div className="earnings-container">
        <div className="earnings-header">
          <h2>Earnings & Payouts</h2>
          <p>Manage your provider earnings and request payouts</p>
        </div>

        {/* Stats Overview */}
        <div className="earnings-stats">
          <h3>Earnings Summary</h3>
          
          <div className="stats-grid">
            <div className="stat-card total-earnings">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <div className="stat-value">{formatCurrency(stats.totalEarned)}</div>
                <div className="stat-label">Total Earned</div>
              </div>
            </div>

            <div className="stat-card settled-balance">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-value">{formatCurrency(stats.settledAmount)}</div>
                <div className="stat-label">Settled Balance</div>
                <div className="stat-sublabel">{stats.settledCount} payouts</div>
              </div>
            </div>

            <div className="stat-card not-settled-balance">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <div className="stat-value">{formatCurrency(stats.notSettledAmount)}</div>
                <div className="stat-label">Not Settled Balance</div>
                <div className="stat-sublabel">{stats.notSettledCount} payouts</div>
              </div>
            </div>

            <div className="stat-card paid-amount">
              <div className="stat-icon">🏦</div>
              <div className="stat-content">
                <div className="stat-value">{formatCurrency(stats.paidAmount)}</div>
                <div className="stat-label">Already Paid Out</div>
                <div className="stat-sublabel">{stats.paidCount} payouts</div>
              </div>
            </div>
          </div>
        </div>

        {/* Payout Actions */}
        <div className="payout-actions">
          <h3>Request Payout</h3>
          <div className="payout-info">
            <p><strong>Settled Funds:</strong> Available for immediate payout with no fees</p>
            {stats.notSettledAmount > 0 && (
              <p><strong>Not Settled Funds:</strong> Available for early payout with 2% processing fee</p>
            )}
          </div>
          
          <div className="payout-buttons">
            <div className="payout-option">
              <div className="payout-option-info">
                <h4>Settled Payout</h4>
                <p className="payout-amount">{formatCurrency(stats.settledAmount)}</p>
                <p className="payout-description">No fees • Funds settled 7+ business days ago</p>
              </div>
              <button 
                onClick={() => handleProcessPayout('settled')}
                disabled={processingPayout || stats.settledAmount <= 0}
                className="btn-payout settled"
              >
                {processingPayout ? 'Processing...' : 'Request Settled Payout'}
              </button>
            </div>

            <div className="payout-option">
              <div className="payout-option-info">
                <h4>All Available Payout</h4>
                <p className="payout-amount">{formatCurrency(stats.settledAmount + stats.notSettledAmount)}</p>
                <p className="payout-description">
                  {stats.notSettledAmount > 0 ? (
                    <>
                      2% fee • Includes not yet settled funds
                      <span> (Fee: {formatCurrency((stats.settledAmount + stats.notSettledAmount) * 0.02)})</span>
                    </>
                  ) : (
                    "No fees • All funds are settled"
                  )}
                </p>
              </div>
              <button 
                onClick={() => handleProcessPayout('not_settled')}
                disabled={processingPayout || (stats.settledAmount + stats.notSettledAmount) <= 0}
                className="btn-payout not-settled"
              >
                {processingPayout ? 'Processing...' : 'Request All Available'}
              </button>
            </div>
          </div>
        </div>

        {/* Payout History */}
        <div className="payout-history">
          <h3>Payout History</h3>
          {getVisiblePayouts().length === 0 ? (
            <div className="no-payouts">
              <div className="no-payouts-icon">💰</div>
              <h4>No Payouts Yet</h4>
              <p>Your payouts will appear here once you complete sessions.</p>
            </div>
          ) : (
            <div className="payouts-table">
              <div className="table-header">
                <div className="col-date">Created</div>
                <div className="col-amount">Amount</div>
                <div className="col-status">Status</div>
                <div className="col-settled">Settlement Date</div>
                <div className="col-session">Session</div>
              </div>
              {getVisiblePayouts().slice(0, 20).map(payout => {
                const settledDate = payout.settledDate?.seconds 
                  ? new Date(payout.settledDate.seconds * 1000)
                  : new Date(payout.settledDate);
                const createdDate = payout.createdAt?.seconds 
                  ? new Date(payout.createdAt.seconds * 1000)
                  : new Date(payout.createdAt);

                return (
                  <div key={payout.id} className="table-row">
                    <div className="col-date">{formatDate(createdDate)}</div>
                    <div className="col-amount">{formatCurrency(payout.amount)}</div>
                    <div className={`col-status status-${payout.status.toLowerCase().replace(' ', '-')}`}>
                      {payout.status}
                    </div>
                    <div className="col-settled">{formatDate(settledDate)}</div>
                    <div className="col-session">{payout.sessionId ? '✓' : '—'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EarningsDashboard;